import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  AlertCircle,
  Send,
  RefreshCw,
} from "lucide-react";

import { api } from "../../../../config/api";
import adminService from "../../../../services/adminService";

import "./Admindocuments.scss";

const TABS = [
  { key: "pendentes", label: "Pendentes", icon: Clock },
  { key: "aprovados", label: "Aprovados", icon: CheckCircle },
  { key: "rejeitados", label: "Rejeitados", icon: XCircle },
  { key: "todos", label: "Todos", icon: FileText },
];

const STATUS_CONFIG = {
  pending: {
    label: "Pendente",
    className: "pending",
    icon: Clock,
  },
  approved: {
    label: "Aprovado",
    className: "approved",
    icon: CheckCircle,
  },
  rejected: {
    label: "Rejeitado",
    className: "rejected",
    icon: XCircle,
  },
};

export default function AdminDocuments({ onChanged }) {
  const [tab, setTab] = useState("pendentes");

  const [documents, setDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);

  const [loading, setLoading] = useState(true);

  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [error, setError] = useState(null);

  const [documentPreviews, setDocumentPreviews] = useState({});
  const [documentLoading, setDocumentLoading] = useState({});
  const [documentErrors, setDocumentErrors] = useState({});

  /*
   * Guarda os blob URLs criados para podermos revogá-los
   * quando não forem mais necessários.
   */
  const objectUrlsRef = useRef({});

  /*
   * Controla quais documentos já foram solicitados.
   *
   * IMPORTANTE:
   * Não usamos documentPreviews/documentLoading como dependências
   * do useEffect que dispara o carregamento, evitando o loop infinito.
   */
  const loadedDocumentsRef = useRef(new Set());

  /*
   * Revoga um blob URL específico.
   */
  const revokeObjectUrl = useCallback((key) => {
    const objectUrl = objectUrlsRef.current[key];

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      delete objectUrlsRef.current[key];
    }
  }, []);

  /*
   * Limpa completamente os estados de um usuário.
   */
  const resetDocumentStateForUser = useCallback(
    (userKey) => {
      if (!userKey) return;

      const frontKey = `${userKey}-front`;
      const backKey = `${userKey}-back`;

      revokeObjectUrl(frontKey);
      revokeObjectUrl(backKey);

      loadedDocumentsRef.current.delete(frontKey);
      loadedDocumentsRef.current.delete(backKey);

      setDocumentPreviews((prev) => {
        const next = { ...prev };

        delete next[frontKey];
        delete next[backKey];

        return next;
      });

      setDocumentErrors((prev) => {
        const next = { ...prev };

        delete next[frontKey];
        delete next[backKey];

        return next;
      });

      setDocumentLoading((prev) => {
        const next = { ...prev };

        delete next[frontKey];
        delete next[backKey];

        return next;
      });
    },
    [revokeObjectUrl]
  );

  /*
   * Carrega um documento através do endpoint protegido.
   *
   * O backend retorna o arquivo.
   * O frontend transforma o Blob em blob:http://...
   *
   * Isso é esperado e é exatamente o que queremos:
   * a URL original do Cloudinary não fica exposta no navegador.
   */
  const loadProtectedDocument = useCallback(
    async (doc, side) => {
      const userKey = doc.userId || doc.id;

      if (!userKey) {
        return;
      }

      const key = `${userKey}-${side}`;

      const documentUrl =
        side === "front"
          ? doc.documentUrlFront
          : doc.documentUrlBack;

      if (!documentUrl) {
        return;
      }

      /*
       * Evita requisições duplicadas.
       */
      if (loadedDocumentsRef.current.has(key)) {
        return;
      }

      /*
       * O backend deve devolver algo como:
       *
       * /api/cloudinary/document/{userId}?side=front
       */
      const isProtectedRoute =
        /\/api\/cloudinary\/document\//i.test(documentUrl);

      if (!isProtectedRoute) {
        setDocumentErrors((prev) => ({
          ...prev,
          [key]: "Documento indisponível.",
        }));

        loadedDocumentsRef.current.add(key);

        return;
      }

      /*
       * Marca ANTES da requisição para impedir que outro render
       * dispare uma segunda chamada.
       */
      loadedDocumentsRef.current.add(key);

      setDocumentLoading((prev) => ({
        ...prev,
        [key]: true,
      }));

      setDocumentErrors((prev) => ({
        ...prev,
        [key]: null,
      }));

      try {
        const response = await api.get(documentUrl, {
          responseType: "blob",
        });

        /*
         * Cria URL temporária local.
         *
         * Exemplo:
         * blob:http://localhost:5173/ff8bacbd-...
         */
        const blobUrl = URL.createObjectURL(response.data);

        /*
         * Se já existia um blob anterior, revoga antes de substituir.
         */
        revokeObjectUrl(key);

        objectUrlsRef.current[key] = blobUrl;

        setDocumentPreviews((prev) => ({
          ...prev,
          [key]: blobUrl,
        }));
      } catch (error) {
        /*
         * Se falhou, permitimos uma nova tentativa posteriormente.
         */
        loadedDocumentsRef.current.delete(key);

        const status = error?.response?.status;

        const message =
          status === 401 || status === 403
            ? "Acesso negado ao documento."
            : "Não foi possível carregar o documento.";

        setDocumentErrors((prev) => ({
          ...prev,
          [key]: message,
        }));
      } finally {
        setDocumentLoading((prev) => ({
          ...prev,
          [key]: false,
        }));
      }
    },
    [revokeObjectUrl]
  );

  /*
   * Limpeza completa quando o componente desmontar.
   */
  useEffect(() => {
    return () => {
      Object.keys(objectUrlsRef.current).forEach((key) => {
        URL.revokeObjectURL(objectUrlsRef.current[key]);
      });

      objectUrlsRef.current = {};
      loadedDocumentsRef.current.clear();
    };
  }, []);

  /*
   * Carrega os documentos protegidos quando a lista muda.
   *
   * ATENÇÃO:
   * documentPreviews e documentLoading NÃO são dependências.
   *
   * Isso elimina o loop:
   *
   * effect
   *   -> setDocumentLoading
   *   -> render
   *   -> effect
   *   -> setDocumentLoading
   *   -> render
   *   -> ...
   */
  useEffect(() => {
    if (!Array.isArray(documents) || documents.length === 0) {
      return;
    }

    documents.forEach((doc) => {
      const userKey = doc.userId || doc.id;

      if (!userKey) {
        return;
      }

      const frontUrl = doc.documentUrlFront;
      const backUrl = doc.documentUrlBack;

      if (!frontUrl && !backUrl) {
        resetDocumentStateForUser(userKey);
        return;
      }

      if (frontUrl) {
        loadProtectedDocument(doc, "front");
      }

      if (backUrl) {
        loadProtectedDocument(doc, "back");
      }
    });
  }, [documents, loadProtectedDocument, resetDocumentStateForUser]);

  /*
   * Filtra os documentos conforme a aba.
   *
   * Essa função NÃO precisa ser useCallback porque não é usada
   * como dependência de nenhum useEffect.
   */
  const updateDocumentsForTab = (docs, currentTab) => {
    if (!Array.isArray(docs)) {
      setDocuments([]);
      return;
    }

    if (currentTab === "todos") {
      setDocuments(docs);
      return;
    }

    const statusMap = {
      pendentes: "pending",
      aprovados: "approved",
      rejeitados: "rejected",
    };

    const status = statusMap[currentTab];

    const filtered = docs.filter(
      (d) =>
        d.documentStatus?.toLowerCase().trim() === status
    );

    setDocuments(filtered);
  };

  /*
   * Carrega todos os documentos.
   */
  const loadAllDocuments = useCallback(async () => {
    setError(null);

    try {
      const data = await adminService.getDocuments("todos");

      let documentsData = data;

      if (
        data &&
        data.data &&
        Array.isArray(data.data)
      ) {
        documentsData = data.data;
      } else if (
        data &&
        data.documents &&
        Array.isArray(data.documents)
      ) {
        documentsData = data.documents;
      } else if (
        data &&
        data.items &&
        Array.isArray(data.items)
      ) {
        documentsData = data.items;
      }

      if (!Array.isArray(documentsData)) {
        console.error(
          "❌ Dados não são um array:",
          documentsData
        );

        setAllDocuments([]);
        setDocuments([]);

        return;
      }

      /*
       * Como a lista foi recarregada, precisamos permitir
       * que os documentos sejam buscados novamente.
       */
      loadedDocumentsRef.current.clear();

      /*
       * Revoga os blobs antigos.
       */
      Object.keys(objectUrlsRef.current).forEach((key) => {
        URL.revokeObjectURL(objectUrlsRef.current[key]);
      });

      objectUrlsRef.current = {};

      setDocumentPreviews({});
      setDocumentErrors({});
      setDocumentLoading({});

      setAllDocuments(documentsData);

      updateDocumentsForTab(
        documentsData,
        tab
      );
    } catch (error) {
      console.error(
        "❌ Erro ao carregar documentos:",
        error
      );

      setError(
        error.message ||
          "Erro ao carregar documentos"
      );
    } finally {
      setLoading(false);
    }
  }, [tab]);

  /*
   * Carrega inicialmente.
   */
  useEffect(() => {
    loadAllDocuments();
  }, [loadAllDocuments]);

  /*
   * Quando muda a aba, apenas filtra os dados existentes.
   *
   * Não faz nova requisição.
   */
  useEffect(() => {
    if (Array.isArray(allDocuments)) {
      updateDocumentsForTab(
        allDocuments,
        tab
      );
    }
  }, [tab, allDocuments]);

  /*
   * Recarrega a lista.
   */
  const reload = async () => {
    setLoading(true);

    await loadAllDocuments();

    onChanged?.();
  };

  /*
   * Aprovar documento.
   */
  const approve = async (userId) => {
    try {
      await adminService.approveDocument(userId);

      await reload();
    } catch (error) {
      console.error(
        "Erro ao aprovar documento:",
        error
      );

      alert(
        "Erro ao aprovar documento. Tente novamente."
      );
    }
  };

  /*
   * Abre formulário de rejeição.
   */
  const openReject = (userId) => {
    setRejectingId(userId);
    setRejectReason("");
  };

  /*
   * Confirma rejeição.
   */
  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      alert(
        "Por favor, informe o motivo da rejeição."
      );

      return;
    }

    try {
      await adminService.rejectDocument(
        rejectingId,
        rejectReason
      );

      setRejectingId(null);
      setRejectReason("");

      await reload();
    } catch (error) {
      console.error(
        "Erro ao rejeitar documento:",
        error
      );

      alert(
        "Erro ao rejeitar documento. Tente novamente."
      );
    }
  };

  /*
   * Configuração visual do status.
   */
  const getStatusConfig = (status) => {
    if (!status) {
      return STATUS_CONFIG.pending;
    }

    const normalizedStatus =
      status.toLowerCase().trim();

    return (
      STATUS_CONFIG[normalizedStatus] ||
      STATUS_CONFIG.pending
    );
  };

  /*
   * Formatação da data.
   */
  const formatDate = (dateStr) => {
    if (!dateStr) {
      return "N/A";
    }

    return new Date(dateStr).toLocaleDateString(
      "pt-BR",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  /*
   * Nome amigável do tipo de documento.
   */
  const getDocumentLabel = (type) => {
    const labels = {
      cpf: "CPF",
      cnpj: "CNPJ",
      rg: "RG",
      identity: "Identidade",
      driver_license: "CNH",
      passport: "Passaporte",
      address_proof:
        "Comprovante de Endereço",
    };

    return (
      labels[type] ||
      type ||
      "Documento"
    );
  };

  /*
   * Contagem das abas.
   */
  const getTabCount = (key) => {
    if (!Array.isArray(allDocuments)) {
      return 0;
    }

    if (key === "todos") {
      return allDocuments.length;
    }

    const statusMap = {
      pendentes: "pending",
      aprovados: "approved",
      rejeitados: "rejected",
    };

    const status = statusMap[key];

    return allDocuments.filter(
      (d) =>
        d.documentStatus
          ?.toLowerCase()
          .trim() === status
    ).length;
  };

  /*
   * Estado de erro geral.
   */
  if (error) {
    return (
      <div className="admin-documents">
        <div className="admin-documents__error">
          <AlertCircle size={40} />

          <h3>
            Erro ao carregar documentos
          </h3>

          <p>{error}</p>

          <button onClick={reload}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-documents">

      {/* Header */}
      <div className="admin-documents__header">
        <div className="admin-documents__title">
          <FileText size={20} />
          Documentos
        </div>

        <div className="admin-documents__header-actions">
          <button
            className="admin-documents__btn admin-documents__btn--refresh"
            onClick={reload}
            title="Atualizar"
            disabled={loading}
          >
            <RefreshCw
              size={16}
              className={
                loading
                  ? "spinning"
                  : ""
              }
            />
          </button>

          <span className="admin-documents__count">
            Total{" "}
            <span>
              {allDocuments.length}
            </span>
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-documents__tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count =
            getTabCount(t.key);

          return (
            <button
              key={t.key}
              className={
                tab === t.key
                  ? "active"
                  : ""
              }
              onClick={() =>
                setTab(t.key)
              }
            >
              <Icon size={14} />

              {t.label}

              <span className="count">
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {loading && (
        <div className="admin-documents__loading">
          Carregando documentos...
        </div>
      )}

      {/* Lista */}
      {!loading &&
        Array.isArray(documents) &&
        documents.length > 0 && (
          <div className="admin-documents__list">

            {documents.map(
              (doc, index) => {
                const statusConfig =
                  getStatusConfig(
                    doc.documentStatus
                  );

                const StatusIcon =
                  statusConfig.icon;

                const userKey =
                  doc.userId ||
                  doc.id ||
                  index;

                const frontKey =
                  `${userKey}-front`;

                const backKey =
                  `${userKey}-back`;

                const isPending =
                  doc.documentStatus
                    ?.toLowerCase()
                    .trim() ===
                  "pending";

                const isRejected =
                  doc.documentStatus
                    ?.toLowerCase()
                    .trim() ===
                  "rejected";

                const isApproved =
                  doc.documentStatus
                    ?.toLowerCase()
                    .trim() ===
                  "approved";

                return (
                  <div
                    className={`admin-documents__item admin-documents__item--${statusConfig.className}`}
                    key={
                      doc.userId ||
                      doc.id ||
                      index
                    }
                  >

                    {/* Topo */}
                    <div className="admin-documents__item-top">

                      <div className="admin-documents__item-info">

                        <div className="admin-documents__item-name">
                          {doc.userName ||
                            doc.name ||
                            "Usuário"}
                        </div>

                        <div className="admin-documents__item-meta">

                          <span>
                            <FileText size={14} />

                            {getDocumentLabel(
                              doc.documentType
                            )}
                          </span>

                          <span>•</span>

                          <span>
                            <Calendar size={14} />

                            Enviado em{" "}
                            {formatDate(
                              doc.submittedAt
                            )}
                          </span>

                          <span>•</span>

                          <span>
                            <User size={14} />

                            ID:{" "}
                            {doc.userId ||
                              doc.id ||
                              "N/A"}
                          </span>

                        </div>
                      </div>

                      <span
                        className={`admin-documents__item-status admin-documents__item-status--${statusConfig.className}`}
                      >
                        <StatusIcon size={12} />

                        {statusConfig.label}
                      </span>

                    </div>

                    {/* Thumbnails */}
                    {(doc.documentUrlFront ||
                      doc.documentUrlBack) && (
                      <div className="admin-documents__thumbs">

                        {/* Frente */}
                        {doc.documentUrlFront && (
                          <div className="admin-documents__thumb">

                            {documentLoading[
                              frontKey
                            ] ? (
                              <div className="admin-documents__thumb-loading">
                                Carregando...
                              </div>
                            ) : documentErrors[
                                frontKey
                              ] ? (
                              <div className="admin-documents__thumb-error">
                                {
                                  documentErrors[
                                    frontKey
                                  ]
                                }
                              </div>
                            ) : documentPreviews[
                                frontKey
                              ] ? (
                              <a
                                href={
                                  documentPreviews[
                                    frontKey
                                  ]
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={
                                    documentPreviews[
                                      frontKey
                                    ]
                                  }
                                  alt="Frente do documento"
                                />
                              </a>
                            ) : null}

                            <span>
                              Frente
                            </span>
                          </div>
                        )}

                        {/* Verso */}
                        {doc.documentUrlBack && (
                          <div className="admin-documents__thumb">

                            {documentLoading[
                              backKey
                            ] ? (
                              <div className="admin-documents__thumb-loading">
                                Carregando...
                              </div>
                            ) : documentErrors[
                                backKey
                              ] ? (
                              <div className="admin-documents__thumb-error">
                                {
                                  documentErrors[
                                    backKey
                                  ]
                                }
                              </div>
                            ) : documentPreviews[
                                backKey
                              ] ? (
                              <a
                                href={
                                  documentPreviews[
                                    backKey
                                  ]
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={
                                    documentPreviews[
                                      backKey
                                    ]
                                  }
                                  alt="Verso do documento"
                                />
                              </a>
                            ) : null}

                            <span>
                              Verso
                            </span>
                          </div>
                        )}

                      </div>
                    )}

                    {/* Motivo da rejeição */}
                    {isRejected &&
                      doc.documentRejectionReason && (
                        <div className="admin-documents__item-rejection">

                          <AlertCircle size={16} />

                          <div>
                            <strong>
                              Motivo da rejeição:
                            </strong>{" "}
                            {
                              doc.documentRejectionReason
                            }
                          </div>

                        </div>
                      )}

                    {/* Ações */}
                    {isPending && (
                      <div className="admin-documents__item-actions">

                        <button
                          className="admin-documents__btn admin-documents__btn--approve"
                          onClick={() =>
                            approve(
                              doc.userId
                            )
                          }
                        >
                          <CheckCircle size={14} />

                          Aprovar
                        </button>

                        <button
                          className="admin-documents__btn admin-documents__btn--reject"
                          onClick={() =>
                            openReject(
                              doc.userId
                            )
                          }
                        >
                          <XCircle size={14} />

                          Rejeitar
                        </button>

                      </div>
                    )}

                    {/* Aprovado */}
                    {isApproved && (
                      <div className="admin-documents__item-approved">

                        <CheckCircle
                          size={16}
                          color="#10b981"
                        />

                        <span>
                          Documento aprovado em{" "}
                          {formatDate(
                            doc.approvedAt ||
                              doc.submittedAt
                          )}
                        </span>

                      </div>
                    )}

                    {/* Caixa de rejeição */}
                    {rejectingId ===
                      doc.userId && (
                      <div className="admin-documents__reject-box">

                        <textarea
                          className="admin-documents__reject-textarea"
                          placeholder="Explique o que precisa ser corrigido (ex: foto ilegível, documento vencido, dados inconsistentes...)"
                          value={
                            rejectReason
                          }
                          onChange={(e) =>
                            setRejectReason(
                              e.target.value
                            )
                          }
                          rows={3}
                        />

                        <div className="admin-documents__reject-actions">

                          <button
                            className="admin-documents__btn admin-documents__btn--outline"
                            onClick={() =>
                              setRejectingId(
                                null
                              )
                            }
                          >
                            Cancelar
                          </button>

                          <button
                            className="admin-documents__btn admin-documents__btn--reject"
                            onClick={
                              confirmReject
                            }
                          >
                            <Send size={14} />

                            Confirmar rejeição
                          </button>

                        </div>

                      </div>
                    )}

                  </div>
                );
              }
            )}

          </div>
        )}

      {/* Empty State */}
      {!loading &&
        (!Array.isArray(documents) ||
          documents.length === 0) && (
          <div className="admin-documents__empty">

            <FileText size={40} />

            <h3>
              Nenhum documento encontrado
            </h3>

            <p>
              {tab === "pendentes"
                ? "Não há documentos aguardando aprovação."
                : tab === "aprovados"
                ? "Não há documentos aprovados neste momento."
                : tab === "rejeitados"
                ? "Não há documentos rejeitados."
                : "Nenhum documento foi enviado ainda."}
            </p>

            <button
              className="admin-documents__btn admin-documents__btn--refresh"
              onClick={reload}
            >
              Atualizar
            </button>

          </div>
        )}

    </div>
  );
}